"use client";

import { useLazyQuery, useMutation, useQuery, useSubscription } from "@apollo/client";
import AvatarWithInitials from "components/core/AvatarWithInitials/AvatarWithInitials";
import HTML from "components/ui/v2/html-content-v2";
import { MentionsRichTextEditorV2 as Note3 } from "components/ui/v2/mentions-rich-text-editor-v2";
import UserAvatar from "components/ui/v2/user-avatar-compat-v2";
import {
  ExploreCommentLikeButtonV2,
  ExploreCommentMessageReactionV2,
  ExploreCommentReactionCountV2
} from "./ExploreCommentMessageReactionV2";
import {
  ImageZoom,
  resolveImageZoomTarget,
  type ImageZoomRect
} from "components/ui/v2/image-zoom";
import { ExploreCommentMediaPreviewV2 } from "./ExploreCommentMediaPreviewV2";
import { ExploreReactionsModalV2 } from "./ExploreReactionsModalV2";
import { ExploreViewCounterV2 } from "./ExploreViewCounterV2";
import {
  ReactionBurstOverlay,
  useReactionBurst
} from "./reaction-burst-overlay";
import { MultiSelectFilterV2 } from "components/ui/v2/composites/multi-select-filter-v2";
import { LiquidPillDropdown } from "components/ui/v2/liquid-pill-dropdown";
import { LiquidPillGroup } from "components/ui/v2/liquid-pill-group";
import { Modal } from "components/ui/v2/modal";
import { Z_INDEX } from "components/ui/v2/z-index";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import escapeHtml from "escape-html";
import { useChatUserDirectory } from "components/Chat/useChatUserDirectory";
import { gqlFeedback, gqlMessage } from "gql";
import { CREATE_CONVERSATION, SEND_MESSAGE } from "gql/Conversation/mutations";
import { GET_CONVERSATIONS } from "gql/Conversation/queries";
import { deleteClipboardImageFromStorage } from "utils/uploadClipboardImage";
import { useExperienceGain } from "hooks/useExperienceGain";
import moment from "moment";
import { useTheme } from "next-themes";
import useTranslation from "next-translate/useTranslation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  AvatarFallback,
  Avatar as ShadcnAvatar
} from "shadcn/components/ui/avatar";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import ReactTimeAgo from "react-time-ago";
import { Text } from "slate";
import { Button, isRenderableImageSrc } from "components/ui/v2";
import {
  EMessageType,
  IMessageComment,
  IMessageReaction
} from "types/Message.type";
import { IUser } from "types/User.types";
import {
  extractSlateImageUrls,
  hasCommentContent,
  serializeNode
} from "utils/string";
import { ExternalLink, MessageCircle, Pencil, Send, Share2, Sparkles, Trash2, User, X } from "lucide-react";

/* ─── Types ──────────────────────────────────────────────────────────────── */

export interface FeedCardFooterProps {
  messageId: string;
  reactions: IMessageReaction[];
  comments: IMessageComment[];
  commentsLength?: number;
  usersToMention?: IUser[];
  variant?: "default" | "corporate" | "acknowledgement";
  onDeleteMessage?: () => void;
  deleteLoading?: boolean;
  preview?: boolean;
  timesSeen?: number;
  type?: EMessageType;
}

interface IForm {
  message: string;
}

/* ─── Reaction avatar ────────────────────────────────────────────────────── */

const EMOJI_MAP: Record<string, string> = {
  like: "👍",
  love: "❤️",
  haha: "😂",
  wow: "😮",
  sad: "😢",
  angry: "😡"
};

const INLINE_REACTIONS = ["like", "love", "haha", "wow", "sad", "angry"];

const POP_DURATION_MS = 480;
const GIANT_REACTION_EXIT_MS = 560;
const ROTATE_INTERVAL_MS = 2200;

const VISIBLE_COUNT = 10;
const MIN_CAROUSEL_COUNT = 5;
const AVATAR_SIZE_PX = 24;
const AVATAR_GAP_PX = 12;
const SLIDE_STEP_PX = AVATAR_SIZE_PX + AVATAR_GAP_PX;

type QueueItem = {
  id: string;
  reaction: IMessageReaction;
};

function createQueue(reactions: IMessageReaction[]): QueueItem[] {
  return reactions.map((reaction, index) => {
    const reactionOwnerId =
      reaction.user ||
      reaction.reactionUser?.id ||
      `${reaction.reactionUser?.name ?? "unknown"}-${reaction.reactionUser?.lastname ?? "user"}`;

    return {
      id: `${reactionOwnerId}-${reaction.emoji}-${index}`,
      reaction
    };
  });
}

function createTrackItems(
  items: QueueItem[],
  visibleCount: number
): QueueItem[] {
  if (items.length <= 1) return items;

  const wrapCount = Math.min(visibleCount + 1, items.length);
  const wrapItems = items.slice(0, wrapCount).map((item, index) => ({
    id: `${item.id}-wrap-${index}`,
    reaction: item.reaction
  }));

  return [...items, ...wrapItems];
}

function reactionTrackWidth(itemCount: number): number {
  if (itemCount <= 0) return 0;
  return itemCount * AVATAR_SIZE_PX + (itemCount - 1) * AVATAR_GAP_PX;
}

function ReactionAvatarSlider({
  reactions,
  onOpen
}: {
  reactions: IMessageReaction[];
  onOpen: () => void;
}) {
  const items = useMemo(() => createQueue(reactions), [reactions]);
  const count = items.length;
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === "undefined" ? 1024 : window.innerWidth
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener("resize", updateViewportWidth);
    return () => window.removeEventListener("resize", updateViewportWidth);
  }, []);

  const responsiveVisibleLimit =
    viewportWidth < 380
      ? 2
      : viewportWidth < 480
        ? 3
        : viewportWidth < 640
          ? 4
          : VISIBLE_COUNT;
  const shouldAnimate = count >= MIN_CAROUSEL_COUNT;
  const trackItems = useMemo(() => {
    if (count < MIN_CAROUSEL_COUNT) return items;
    return createTrackItems(
      items,
      Math.min(items.length, responsiveVisibleLimit)
    );
  }, [items, count, responsiveVisibleLimit]);
  const visibleCount = Math.min(count, responsiveVisibleLimit);

  const reactionImageUrls = useMemo(
    () => [
      ...new Set(
        reactions
          .map((reaction) => reaction.reactionUser?.profileImage)
          .filter(Boolean) as string[]
      )
    ],
    [reactions]
  );
  const [imagesReady, setImagesReady] = useState(false);

  const [startIndex, setStartIndex] = useState(0);
  const [slideOffset, setSlideOffset] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [showInitialStagger, setShowInitialStagger] = useState(true);
  const startIndexRef = useRef(0);
  const cyclingRef = useRef(false);
  const isSlidingRef = useRef(false);
  const trackRef = useRef<HTMLDivElement>(null);

  startIndexRef.current = startIndex;
  isSlidingRef.current = isSliding;

  useEffect(() => {
    let cancelled = false;

    setImagesReady(reactionImageUrls.length === 0);

    if (reactionImageUrls.length > 0) {
      Promise.allSettled(
        reactionImageUrls.map(
          (url) =>
            new Promise<void>((resolve) => {
              const image = new window.Image();
              image.onload = () => resolve();
              image.onerror = () => resolve();
              image.src = url;
            })
        )
      ).then(() => {
        if (!cancelled) setImagesReady(true);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [reactionImageUrls]);

  useEffect(() => {
    startIndexRef.current = 0;
    setStartIndex(0);
    setSlideOffset(0);
    setIsSliding(false);
    setTransitionEnabled(true);
    cyclingRef.current = false;
    setShowInitialStagger(true);
    const t = window.setTimeout(
      () => setShowInitialStagger(false),
      420 + Math.min(reactions.length, responsiveVisibleLimit) * 55
    );
    return () => window.clearTimeout(t);
  }, [reactions, responsiveVisibleLimit]);

  const finishSlide = useCallback(() => {
    if (!isSlidingRef.current) return;

    const nextIndex = startIndexRef.current + 1;
    const needsSnap = nextIndex >= count;

    if (needsSnap) {
      startIndexRef.current = 0;
      setTransitionEnabled(false);
      setStartIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTransitionEnabled(true));
      });
    } else {
      startIndexRef.current = nextIndex;
      setStartIndex(nextIndex);
    }

    setSlideOffset(0);
    setIsSliding(false);
    cyclingRef.current = false;
  }, [count]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.target !== track || e.propertyName !== "transform") return;
      finishSlide();
    };

    track.addEventListener("transitionend", handleTransitionEnd);
    return () =>
      track.removeEventListener("transitionend", handleTransitionEnd);
  }, [finishSlide]);

  useEffect(() => {
    if (!shouldAnimate) return;

    const interval = window.setInterval(() => {
      if (cyclingRef.current) return;
      if (items.length < MIN_CAROUSEL_COUNT) return;

      cyclingRef.current = true;
      setIsSliding(true);
      requestAnimationFrame(() => setSlideOffset(-SLIDE_STEP_PX));
    }, ROTATE_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [shouldAnimate, count, items.length]);

  useEffect(() => {
    if (!isSliding) return;

    const fallback = window.setTimeout(finishSlide, POP_DURATION_MS + 80);
    return () => window.clearTimeout(fallback);
  }, [isSliding, finishSlide]);

  if (count === 0) return null;

  const clipWidth = reactionTrackWidth(visibleCount);

  if (!imagesReady) {
    return (
      <div className="shrink-0" style={{ width: clipWidth, height: 36 }} />
    );
  }
  const totalTranslate = shouldAnimate
    ? -startIndex * SLIDE_STEP_PX + slideOffset
    : 0;
  const fadeClass = shouldAnimate
    ? " reaction-avatar-slider-clip--fade-right"
    : "";

  return (
    <button
      type="button"
      onClick={onOpen}
      className="reaction-avatar-slider relative block max-w-full shrink-0 cursor-pointer overflow-visible border-0 bg-transparent p-0 transition-transform duration-300 ease-out hover:scale-[1.02] active:scale-[0.98]"
      style={{ width: clipWidth }}
    >
      <div
        className={`reaction-avatar-slider-clip overflow-hidden py-1.5${fadeClass}`}
        style={{ width: clipWidth }}
      >
        <div
          ref={trackRef}
          className={
            shouldAnimate && isSliding && transitionEnabled
              ? "reaction-avatar-track-sliding"
              : ""
          }
          style={{
            display: "flex",
            width: "max-content",
            alignItems: "center",
            gap: AVATAR_GAP_PX,
            transform: `translateX(${totalTranslate}px)`
          }}
        >
          {(shouldAnimate ? trackItems : items).map((item, index) => (
            <div
              key={item.id}
              className={`reaction-avatar-item shrink-0${showInitialStagger && index < visibleCount ? "reaction-avatar-initial" : ""}`}
              style={
                showInitialStagger && index < visibleCount
                  ? { animationDelay: `${index * 55}ms` }
                  : undefined
              }
            >
              <ReactionAvatar
                reaction={item.reaction}
                animateEmoji={showInitialStagger && index < visibleCount}
                emojiDelay={index * 55 + 80}
              />
            </div>
          ))}
        </div>
      </div>
    </button>
  );
}

const ReactionAvatar = React.memo(function ReactionAvatar({
  reaction,
  animateEmoji = false,
  emojiDelay = 0
}: {
  reaction: IMessageReaction;
  animateEmoji?: boolean;
  emojiDelay?: number;
}) {
  const firstName = reaction.reactionUser?.name ?? "";
  const profileImage = reaction.reactionUser?.profileImage;
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [profileImage, reaction.user]);

  const showImage = isRenderableImageSrc(profileImage) && !imgError;

  return (
    <div className="relative transition-transform duration-200 ease-out">
      <ShadcnAvatar className="h-6 w-6 ring-2 ring-white transition-shadow duration-200 dark:ring-zinc-900">
        {showImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={profileImage}
            alt={firstName}
            className="aspect-square h-full w-full object-cover"
            loading="eager"
            decoding="sync"
            onError={() => setImgError(true)}
          />
        ) : (
          <AvatarFallback className="bg-slate-200 dark:bg-zinc-700">
            <User
              className="h-3 w-3 text-slate-400 dark:text-zinc-400"
              strokeWidth={2.25}
            />
          </AvatarFallback>
        )}
      </ShadcnAvatar>
      <span
        className={`absolute -bottom-0.5 -right-0.5 text-[11px] leading-none${animateEmoji ? "reaction-emoji-badge" : ""}`}
        style={animateEmoji ? { animationDelay: `${emojiDelay}ms` } : undefined}
      >
        {EMOJI_MAP[reaction.emoji] ?? "👍"}
      </span>
    </div>
  );
});

/* ─── Helper: serialize Slate JSON → HTML ─────────────────────────────────── */

function useSerializer() {
  const { theme } = useTheme();
  const { user } = useUserStoreContext((s) => ({ user: s.user }));

  return function serialize(node: any): string {
    if (Text.isText(node)) {
      let str = escapeHtml(node.text as string);
      if ((node as any).bold) str = `<strong>${str}</strong>`;
      return str;
    }
    const children = node.children.map((n: any) => serialize(n)).join("");
    return serializeNode(
      node,
      theme ?? "",
      children,
      user?.selectedVendor?.id ?? ""
    );
  };
}

/* ─── Single comment row ─────────────────────────────────────────────────── */

interface CommentRowProps {
  comment: IMessageComment;
  usersToMention?: IUser[];
  onDelete: (id: string) => void;
  deleteLoading: boolean;
  deletingId: string;
  serialize: (node: any) => string;
  lang: string;
}

function CommentRow({
  comment,
  usersToMention,
  onDelete,
  deleteLoading,
  deletingId,
  serialize,
  lang
}: CommentRowProps) {
  const { t } = useTranslation("common");
  const { user } = useUserStoreContext((s) => ({ user: s.user }));
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState(comment.text ?? "");
  const [zoomImage, setZoomImage] = useState<{
    url: string;
    sourceRect: ImageZoomRect;
  } | null>(null);

  const handleCommentImageClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const resolved = resolveImageZoomTarget(target);
      if (!resolved) return;

      setZoomImage({
        url: resolved.src,
        sourceRect: resolved.sourceRect
      });
    },
    []
  );

  const [updateComment, { loading: updateLoading }] = useMutation(
    gqlMessage.mutations.UPDATE_MESSAGE_COMMENT,
    {
      onCompleted: () => {
        setEditOpen(false);
        toast.success(t("Mensaje actualizado correctamente"));
      },
      onError: () => toast.error(t("Error al actualizar el mensaje"))
    }
  );

  let plainHtml = "";
  if (comment.text) {
    try {
      const nodes = JSON.parse(comment.text);
      for (const node of nodes) plainHtml += serialize(node);
    } catch {
      plainHtml = comment.text;
    }
  }

  const canEdit = user?.id === comment.createdBy?.id || !!user?.isi;
  const authorName = [comment.createdBy?.name, comment.createdBy?.lastname]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="group flex items-start gap-1.5 py-0.5">
      {/* Avatar */}
      <div className="mt-0.5 shrink-0">
        <AvatarWithInitials
          size="xs"
          urlImg={comment.createdBy?.profileImage}
          initials={authorName}
        />
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        {editOpen ? (
          <div className="rounded-xl border border-slate-200/70 bg-white dark:border-zinc-700 dark:bg-zinc-900">
            <div className="min-h-[48px] px-2.5 py-1.5 text-[11.5px]">
              <Note3
                onChange={setEditText}
                usersToMention={usersToMention}
                initialValueString={comment.text ?? ""}
                compact
                showIAButton={false}
              />
            </div>
            <div className="flex items-center justify-end gap-1.5 border-t border-slate-100 px-2.5 py-1 dark:border-zinc-800">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => setEditOpen(false)}
              >
                {t("Cancelar")}
              </Button>
              <Button
                size="sm"
                className="h-5 px-2 text-[10px]"
                disabled={editText === comment.text}
                onClick={() =>
                  updateComment({
                    variables: {
                      updateMessageCommentInput: {
                        id: comment.id,
                        text: editText
                      }
                    }
                  })
                }
              >
                {updateLoading ? "..." : t("Guardar")}
              </Button>
            </div>
          </div>
        ) : (
          <ExploreCommentMessageReactionV2
            reactions={comment.reactions ?? []}
            commentMessage={comment.id}
          >
            <div className="relative overflow-visible rounded-2xl bg-slate-50 px-2.5 pb-1 pt-1.5 dark:bg-zinc-800/60">
              <div className="mb-0.5 flex flex-wrap items-baseline gap-1 leading-none">
                <span className="text-[11px] font-semibold text-slate-800 dark:text-zinc-100">
                  {authorName}
                </span>
                <span className="text-[9.5px] text-slate-400 dark:text-zinc-500">
                  ·{" "}
                  {(comment.createdAt || comment.updatedAt) && (
                    <ReactTimeAgo
                      date={moment(
                        comment.createdAt || comment.updatedAt
                      ).toDate()}
                      locale={lang === "es" ? "es-CL" : "en-US"}
                    />
                  )}
                </span>
                {comment.edited && (
                  <span className="text-[9.5px] italic text-slate-400 dark:text-zinc-500">
                    · {t("Edited")}
                  </span>
                )}
              </div>
              <div
                className="prose prose-sm dark:prose-invert max-w-none text-[11.5px] leading-snug [&>*:last-child]:mb-0 [&_img]:cursor-zoom-in [&_img]:rounded-xl [&_img]:transition-opacity hover:[&_img]:opacity-90"
                onClick={handleCommentImageClick}
              >
                <HTML content={plainHtml} />
              </div>
              <ExploreCommentReactionCountV2 />
            </div>
            <ExploreCommentLikeButtonV2 className="ml-2.5" />
          </ExploreCommentMessageReactionV2>
        )}
      </div>

      {/* Hover actions */}
      {canEdit && !editOpen && (
        <div className="mt-1 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <button
            onClick={() => setEditOpen(true)}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
          >
            <Pencil size={11} />
          </button>
          <button
            disabled={deleteLoading && deletingId === comment.id}
            onClick={() => onDelete(comment.id)}
            className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
          >
            <Trash2 size={11} />
          </button>
        </div>
      )}

      <ImageZoom
        open={Boolean(zoomImage)}
        src={zoomImage?.url ?? null}
        sourceRect={zoomImage?.sourceRect ?? null}
        onClose={() => setZoomImage(null)}
        alt={t("Imagen del comentario")}
        closeButtonLabel={t("Cerrar")}
      />
    </div>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────── */

export function FeedCardFooter({
  messageId,
  reactions,
  comments: initialComments,
  commentsLength = 1,
  usersToMention,
  variant = "default",
  onDeleteMessage,
  deleteLoading = false,
  preview = false,
  timesSeen,
  type
}: FeedCardFooterProps) {
  const { t, lang } = useTranslation("common");
  const { user } = useUserStoreContext((s) => ({ user: s.user }));
  const { actions: xpActions } = useExperienceGain();
  const serialize = useSerializer();

  /* ── Reactions state ── */
  const [localReactions, setLocalReactions] = useState<IMessageReaction[]>(
    reactions ?? []
  );
  const [isReactionsModalOpen, setIsReactionsModalOpen] = useState(false);
  const { reactionBurst, triggerReactionBurst } = useReactionBurst();
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalReactions(reactions ?? []);
  }, [reactions]);

  const [messageReaction] = useMutation(gqlMessage.mutations.MESSAGE_REACTION, {
    onCompleted: ({ messageReaction }: any) =>
      setLocalReactions(messageReaction)
  });

  const userReactionValue = localReactions.find(
    (reaction) =>
      reaction.user === user?.id || reaction.reactionUser?.id === user?.id
  )?.emoji;
  const userReaction = Boolean(userReactionValue);

  const getReactionOwnerId = (reaction: IMessageReaction) =>
    reaction.user || reaction.reactionUser?.id || "";

  const uniqueReactions = localReactions.filter((reaction, idx, self) => {
    const ownerId = getReactionOwnerId(reaction);

    return (
      idx ===
      self.findIndex(
        (item) =>
          getReactionOwnerId(item) === ownerId && item.emoji === reaction.emoji
      )
    );
  });

  const handleReactionSelect = (emoji: string) => {
    const updated = [
      ...localReactions.filter((r) => r.user !== user?.id),
      { emoji, user: user?.id ?? "" }
    ];

    setLocalReactions(updated as IMessageReaction[]);
    triggerReactionBurst(emoji, footerRef.current);

    if (!userReaction) xpActions.likeMessage();
    messageReaction({
      variables: { messageReactionInput: { emoji, message: messageId } }
    });
  };

  const handleRemoveReaction = () => {
    if (!userReaction) return;
    setLocalReactions(localReactions.filter((r) => r.user !== user?.id));
    messageReaction({
      variables: { messageReactionInput: { emoji: "none", message: messageId } }
    });
  };

  /* ── Share state ── */
  const [shareWithColleagueOpen, setShareWithColleagueOpen] = useState(false);
  const [selectedShareColleagueIds, setSelectedShareColleagueIds] = useState<
    string[]
  >([]);
  const { users: chatUsers, loading: chatUsersLoading } =
    useChatUserDirectory();
  const { data: conversationsData } = useQuery(GET_CONVERSATIONS, {
    fetchPolicy: "cache-first",
    errorPolicy: "all"
  });
  const [createConversation, { loading: creatingShareConversation }] =
    useMutation(CREATE_CONVERSATION, { refetchQueries: [GET_CONVERSATIONS] });
  const [sendConversationMessage, { loading: sendingSharedMessage }] =
    useMutation(SEND_MESSAGE);
  const [publishMessageToMyStory, { loading: publishingToMyStory }] =
    useMutation(gqlMessage.mutations.PUBLISH_MESSAGE_TO_MY_STORY);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return `/app/thread/${messageId}`;
    return `${window.location.origin}/app/thread/${messageId}`;
  }, [messageId]);

  const chatUserOptions = useMemo(
    () =>
      chatUsers.map((chatUser) => ({
        value: chatUser.id,
        label: `${chatUser.name || ""} ${chatUser.lastname || ""}`.trim()
      })),
    [chatUsers]
  );

  const handleCloseShareWithColleague = () => {
    setShareWithColleagueOpen(false);
    setSelectedShareColleagueIds([]);
  };

  const handleShareElsewhere = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ url: shareUrl });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t("Enlace copiado al portapapeles"));
        return;
      }

      window.open(shareUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;

      try {
        await navigator.clipboard?.writeText(shareUrl);
        toast.success(t("Enlace copiado al portapapeles"));
      } catch {
        toast.error(t("No se pudo compartir el enlace"));
      }
    }
  };

  const handlePublishToMyStory = async () => {
    try {
      await publishMessageToMyStory({
        variables: { messageId },
        update: (cache, { data: mutationData }) => {
          const created = mutationData?.publishMessageToMyStory as
            | { id: string }
            | undefined;
          if (!created) return;

          try {
            const existing = cache.readQuery<{ getStories: { id: string }[] }>({
              query: gqlMessage.queries.GET_STORIES
            });
            const alreadyInCache = existing?.getStories?.some(
              (story) => story.id === created.id
            );
            if (alreadyInCache) return;

            cache.writeQuery({
              query: gqlMessage.queries.GET_STORIES,
              data: {
                getStories: [created, ...(existing?.getStories || [])]
              }
            });
          } catch {
            // Cache miss — refetch below if needed.
          }
        },
        refetchQueries: [{ query: gqlMessage.queries.GET_STORIES }]
      });

      toast.success(t("Publicado en tu historia"));
    } catch (error) {
      console.error("Failed to publish message to story", error);
      toast.error(t("No se pudo publicar en tu historia"));
    }
  };

  const findExistingConversationWith = (userId: string) => {
    const conversations = conversationsData?.conversations ?? [];
    return conversations.find(
      (conversation: any) =>
        !conversation.isGroup &&
        (conversation.participants?.length ?? 0) === 2 &&
        conversation.participants.some(
          (participant: any) => participant.id === userId
        )
    );
  };

  const handleShareWithColleague = async () => {
    const userId = selectedShareColleagueIds[0];
    if (!userId) return;

    try {
      const existingConversation = findExistingConversationWith(userId);
      let conversationId = existingConversation?.id;

      if (!conversationId) {
        const { data: created } = await createConversation({
          variables: { input: { participantId: userId } }
        });
        conversationId = created?.createConversation?.id;
      }

      if (!conversationId) throw new Error("Conversation was not created");

      await sendConversationMessage({
        variables: {
          input: {
            conversationId,
            text: `${t("Te comparto esta publicación")}: ${shareUrl}`
          }
        },
        refetchQueries: [GET_CONVERSATIONS]
      });

      toast.success(t("Publicación enviada por chat"));
      handleCloseShareWithColleague();
    } catch (error) {
      console.error("Failed to share publication with colleague", error);
      toast.error(t("No se pudo enviar la publicación"));
    }
  };

  const shareDropdownItems = useMemo(
    () => [
      {
        value: "colleague",
        label: t("Compartir con colegas"),
        icon: MessageCircle,
        variant: "indigo" as const
      },
      {
        value: "my-story",
        label: t("Publicar en mi historia"),
        icon: Sparkles,
        variant: "violet" as const,
        disabled: publishingToMyStory
      },
      {
        value: "elsewhere",
        label: t("Compartir en otro lugar"),
        icon: ExternalLink,
        variant: "slate" as const
      }
    ],
    [publishingToMyStory, t]
  );

  const handleShareDropdownSelect = (value: string) => {
    // El dropdown principal siempre vuelve a ser el menú de 2 opciones. Al
    // elegir colega abrimos el selector dedicado y reseteamos su selección.
    if (value === "colleague") {
      setSelectedShareColleagueIds([]);
      setShareWithColleagueOpen(true);
      return;
    }

    if (value === "my-story") {
      handlePublishToMyStory();
      return;
    }

    if (value === "elsewhere") {
      handleShareElsewhere();
    }
  };

  /* ── Comments state ── */
  const [commentList, setCommentList] = useState<IMessageComment[]>(
    initialComments ?? []
  );
  const [showOlder, setShowOlder] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  useEffect(() => {
    setCommentList(initialComments ?? []);
  }, [initialComments]);

  useSubscription(gqlMessage.subscriptions.COMMENT_CREATED, {
    skip: !user,
    variables: { message: messageId },
    onData: ({ data }) => {
      const created = data?.data?.commentCreated;
      if (created && created.createdBy?.id !== user?.id) {
        setCommentList((prev) => [...prev, created]);
      }
    }
  });

  const [deleteComment, { loading: deleteCommentLoading }] = useMutation(
    gqlMessage.mutations.DELETE_MESSAGE_COMMENT,
    {
      onCompleted: ({ deleteMessageComment }: any) => {
        setCommentList((prev) =>
          prev.filter((c) => c.id !== deleteMessageComment.id)
        );
        toast.success(t("DELETE_MESSAGE_SUCCESS"));
      }
    }
  );

  const handleDeleteComment = (commentId: string) => {
    setDeletingId(commentId);
    deleteComment({ variables: { messageComment: commentId } });
  };

  /* ── Comment input ── */
  const { control, handleSubmit, setValue, watch } = useForm<IForm>();
  const [inputOpen, setInputOpen] = useState(false);
  const [contentEmpty, setContentEmpty] = useState(true);
  const [isUploadingCommentImage, setIsUploadingCommentImage] = useState(false);
  const [pendingCommentImageCount, setPendingCommentImageCount] = useState(0);
  const [removingCommentImageUrl, setRemovingCommentImageUrl] = useState<
    string | null
  >(null);
  const removeCommentImageRef = useRef<((url: string) => Promise<void>) | null>(
    null
  );

  const [getSignedUrl] = useLazyQuery(gqlFeedback.queries.GENERATE_SIGNED_URL, {
    fetchPolicy: "network-only"
  });

  const handleCommentChange = useCallback(
    (value: string) => {
      setValue("message", value);
      setContentEmpty(!hasCommentContent(value));
    },
    [setValue]
  );

  const commentDraft = watch("message");
  const commentPreviewImages = useMemo(
    () => extractSlateImageUrls(commentDraft),
    [commentDraft]
  );

  const handleDeleteCommentImageFromStorage = useCallback(
    async (imageUrl: string) => {
      await deleteClipboardImageFromStorage(imageUrl, async (key) => {
        const result = await getSignedUrl({ variables: { key } });
        const signedUrl = result.data?.getSignedUrl;
        if (!signedUrl) throw new Error("DELETE_FAILED");
        return signedUrl as string;
      });
    },
    [getSignedUrl]
  );

  const handleRemoveCommentImage = useCallback(
    async (imageUrl: string) => {
      if (removingCommentImageUrl || !removeCommentImageRef.current) return;

      setRemovingCommentImageUrl(imageUrl);
      try {
        await removeCommentImageRef.current(imageUrl);
      } finally {
        setRemovingCommentImageUrl(null);
      }
    },
    [removingCommentImageUrl]
  );

  const [createComment, { loading: createLoading }] = useMutation(
    gqlMessage.mutations.CREATE_MESSAGE_COMMENT,
    {
      onCompleted: ({ createMessageComment }: any) => {
        setCommentList((prev) => [...prev, createMessageComment]);
        setValue("message", "");
        setContentEmpty(true);
        setInputOpen(false);
        xpActions.sendMessage();
      }
    }
  );

  const handlePublish = (data: IForm) => {
    if (!hasCommentContent(data.message)) return;

    createComment({
      variables: {
        createMessageCommentInput: { message: messageId, text: data.message }
      }
    });
  };

  /* ── Derived comment lists ── */
  const previewCount = Math.max(commentsLength, 2);
  const olderCount = Math.max(0, commentList.length - previewCount);
  const recentComments = commentList.slice(-previewCount);
  const olderComments = commentList.slice(0, commentList.length - previewCount);

  if (preview) return null;

  const displayReactions = uniqueReactions;
  const handleOpenReactions = () => {
    if (uniqueReactions.length > 0) {
      setIsReactionsModalOpen(true);
    }
  };

  return (
    <>
      <style jsx global>{`
        .reaction-avatar-track-sliding {
          transition: transform 480ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: transform;
        }

        .reaction-avatar-slider-clip--fade-right {
          -webkit-mask-image: linear-gradient(
            to right,
            #000 0%,
            #000 calc(100% - 88px),
            rgb(0 0 0 / 0.72) calc(100% - 56px),
            rgb(0 0 0 / 0.32) calc(100% - 28px),
            transparent 100%
          );
          mask-image: linear-gradient(
            to right,
            #000 0%,
            #000 calc(100% - 88px),
            rgb(0 0 0 / 0.72) calc(100% - 56px),
            rgb(0 0 0 / 0.32) calc(100% - 28px),
            transparent 100%
          );
        }

        .reaction-avatar-slider-clip--fade-left {
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0%,
            rgb(0 0 0 / 0.32) 28px,
            rgb(0 0 0 / 0.72) 56px,
            #000 88px,
            #000 100%
          );
          mask-image: linear-gradient(
            to right,
            transparent 0%,
            rgb(0 0 0 / 0.32) 28px,
            rgb(0 0 0 / 0.72) 56px,
            #000 88px,
            #000 100%
          );
        }

        .reaction-avatar-slider-clip--fade-both {
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0%,
            rgb(0 0 0 / 0.32) 28px,
            rgb(0 0 0 / 0.72) 56px,
            #000 88px,
            #000 calc(100% - 88px),
            rgb(0 0 0 / 0.72) calc(100% - 56px),
            rgb(0 0 0 / 0.32) calc(100% - 28px),
            transparent 100%
          );
          mask-image: linear-gradient(
            to right,
            transparent 0%,
            rgb(0 0 0 / 0.32) 28px,
            rgb(0 0 0 / 0.72) 56px,
            #000 88px,
            #000 calc(100% - 88px),
            rgb(0 0 0 / 0.72) calc(100% - 56px),
            rgb(0 0 0 / 0.32) calc(100% - 28px),
            transparent 100%
          );
        }

        .reaction-avatar-initial {
          animation: reaction-avatar-fade-in 420ms
            cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        @keyframes reaction-avatar-fade-in {
          0% {
            opacity: 0;
            transform: scale(0.76) translateY(5px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .reaction-avatar-item {
          position: relative;
        }

        .reaction-avatar-slider:hover .reaction-avatar-item {
          transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .reaction-avatar-slider:hover .reaction-avatar-item:hover {
          transform: scale(1.14);
          z-index: 2;
        }

        .reaction-emoji-badge {
          animation: reaction-emoji-pop 420ms cubic-bezier(0.34, 1.56, 0.64, 1)
            both;
          transform-origin: center center;
        }

        @keyframes reaction-emoji-pop {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          65% {
            transform: scale(1.28);
            opacity: 1;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .reaction-avatar-enter {
          animation: avatar-pop-entry 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)
            both;
          transform-origin: center center;
          position: relative;
          z-index: 2;
        }

        .reaction-avatar-exit {
          animation: avatar-pop-out 0.45s cubic-bezier(0.4, 0, 0.2, 1) both;
          transform-origin: center center;
          position: relative;
          z-index: 1;
        }

        @keyframes reaction-enter {
          0% {
            transform: translateY(80px) scale(0.2);
            opacity: 0;
          }
          55% {
            transform: translateY(-12px) scale(1.18);
            opacity: 1;
          }
          75% {
            transform: translateY(4px) scale(0.95);
            opacity: 1;
          }
          100% {
            transform: translateY(0px) scale(1);
            opacity: 1;
          }
        }
        @keyframes reaction-orbit {
          0% {
            transform: translate(
              calc(var(--r, 8px) * 1),
              calc(var(--r, 8px) * 0)
            );
          }
          25% {
            transform: translate(
              calc(var(--r, 8px) * 0),
              calc(var(--r, 8px) * -1)
            );
          }
          50% {
            transform: translate(
              calc(var(--r, 8px) * -1),
              calc(var(--r, 8px) * 0)
            );
          }
          75% {
            transform: translate(
              calc(var(--r, 8px) * 0),
              calc(var(--r, 8px) * 1)
            );
          }
          100% {
            transform: translate(
              calc(var(--r, 8px) * 1),
              calc(var(--r, 8px) * 0)
            );
          }
        }

        /* Estilos para la animación de rebote  bounce Notification badge*/
        @keyframes bounce {
          0%,
          20%,
          50%,
          80%,
          100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-8px);
          }
          60% {
            transform: translateY(-4px);
          }
        }

        .bounce-badge-notification .rs-badge-content {
          animation: bounce 1s infinite linear;
          right: -6px;
        }

        /* Global style align text-left default */
        .rs-col {
          text-align: left;
        }

        /* Fix background on open injection link on text editor */
        .tox .tox-dialog-wrap__backdrop--opaque {
          background-color: rgba(0, 0, 0, 0.5) !important;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .spin {
          animation-name: spin;
          animation-duration: 1s;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
        }

        .infinite-spin {
          display: inline-block;
        }

        /* Enhanced animations for TopCard */
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInFromLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-shimmer {
          animation: shimmer 1.5s ease-in-out;
        }

        .animate-fade-in {
          animation: fadeIn 0.6s ease-out forwards;
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }

        .animate-slideInFromLeft {
          animation: slideInFromLeft 0.6s ease-out;
        }

        .animation-delay-1000 {
          animation-delay: 1s;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        /* Modal Backdrop Improvements */
        .rs-modal-backdrop {
          background-color: rgba(0, 0, 0, 0.5) !important;
          backdrop-filter: blur(4px) !important;
        }

        .rs-modal-wrapper {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 1rem !important;
        }

        .rs-modal {
          background: transparent !important;
          box-shadow: none !important;
          border: none !important;
          margin: 0 !important;
        }

        /* ─── Navbar search pill ─── */
        .groow-search-pill .rs-picker-toggle,
        .groow-search-pill .rs-picker-toggle-wrapper {
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          min-height: 30px !important;
          height: 30px !important;
          display: flex !important;
          align-items: center !important;
          width: 100% !important;
          justify-content: center !important;
          padding-left: 1.5rem !important;
          padding-right: 1.5rem !important;
          text-align: center !important;
        }

        .groow-search-pill .rs-picker-toggle-textbox,
        .groow-search-pill .rs-picker-search-input {
          display: flex !important;
          min-width: 0 !important;
          flex: 1 1 auto !important;
          align-items: center !important;
          justify-content: center !important;
        }

        .groow-search-pill .rs-picker-search-input > input {
          width: 100% !important;
          min-width: 0 !important;
          padding: 0 !important;
          border: 0 !important;
          background: transparent !important;
          text-align: center !important;
          font-size: 0.7rem !important;
          line-height: 1.1 !important;
          color: rgb(51 65 85 / 0.96) !important;
        }

        .groow-search-pill .rs-picker-toggle-placeholder,
        .groow-search-pill .rs-picker-toggle-value {
          font-size: 0.7rem !important;
          line-height: 1 !important;
          color: rgb(100 116 139 / 0.95) !important;
          width: 100% !important;
          text-align: center !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        /* pill container */
        .groow-search-pill {
          border-radius: 9999px !important;
          min-height: 30px !important;
          background-color: rgb(255 255 255 / 0.5) !important;
          border: 1px solid rgb(226 232 240 / 0.42) !important;
          transition:
            border-color 180ms ease,
            box-shadow 180ms ease,
            background-color 180ms ease;
        }

        .groow-search-pill .rs-picker-toggle-caret,
        .groow-search-pill .rs-picker-toggle-clean,
        .groow-search-pill .rs-picker-toggle-indicator,
        .groow-search-pill .rs-picker-toggle-indicator-group {
          display: none !important;
        }

        .dark .groow-search-pill {
          background-color: rgb(24 24 27 / 0.45) !important;
          border-color: rgb(63 63 70 / 0.55) !important;
        }

        .groow-search-pill:focus-within {
          background-color: rgb(255 255 255 / 0.78) !important;
          border-color: rgb(148 163 184 / 0.5) !important;
          box-shadow: 0 0 0 2px rgb(148 163 184 / 0.06) !important;
        }

        .dark .groow-search-pill:focus-within {
          background-color: rgb(24 24 27 / 0.72) !important;
          border-color: rgb(113 113 122 / 0.7) !important;
        }

        /* Degradé superior al inicio del scroll: .site-spectrum-scroll::before */

        /* ─── Card glow eclipse: orbiting blobs behind glow cards ─── */
        .card-eclipse-enter {
          animation: cardEclipseEnter 700ms ease-out both;
        }

        .card-eclipse-orbit {
          animation: cardEclipseOrbit 18s linear infinite;
          transform: translateZ(0);
          will-change: transform;
        }

        .card-eclipse-pulse-a {
          animation: cardEclipsePulseA 4.3s cubic-bezier(0.7, 0, 0.2, 1)
            infinite;
        }

        .card-eclipse-pulse-b {
          animation: cardEclipsePulseB 5.1s cubic-bezier(0.55, 0, 0.25, 1)
            infinite;
        }

        .card-eclipse-pulse-c {
          animation: cardEclipsePulseC 4.7s cubic-bezier(0.6, 0, 0.3, 1)
            infinite;
        }

        .card-eclipse-pulse-d {
          animation: cardEclipsePulseD 5.8s cubic-bezier(0.65, 0, 0.2, 1)
            infinite;
        }

        @keyframes cardEclipseEnter {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes cardEclipseOrbit {
          from {
            transform: rotate(0deg) translateZ(0);
          }
          to {
            transform: rotate(360deg) translateZ(0);
          }
        }

        @keyframes cardEclipsePulseA {
          0%,
          100% {
            transform: scale(0.78);
            opacity: 0.56;
          }
          24% {
            transform: scale(1.08);
            opacity: 0.9;
          }
          56% {
            transform: scale(0.92);
            opacity: 0.68;
          }
          82% {
            transform: scale(1.16);
            opacity: 1;
          }
        }

        @keyframes cardEclipsePulseB {
          0%,
          100% {
            transform: scale(0.86);
            opacity: 0.6;
          }
          30% {
            transform: scale(1.15);
            opacity: 0.96;
          }
          62% {
            transform: scale(0.84);
            opacity: 0.7;
          }
          88% {
            transform: scale(1.1);
            opacity: 0.88;
          }
        }

        @keyframes cardEclipsePulseC {
          0%,
          100% {
            transform: scale(0.74);
            opacity: 0.52;
          }
          20% {
            transform: scale(1.2);
            opacity: 1;
          }
          58% {
            transform: scale(0.9);
            opacity: 0.64;
          }
          76% {
            transform: scale(1.06);
            opacity: 0.84;
          }
        }

        @keyframes cardEclipsePulseD {
          0%,
          100% {
            transform: scale(0.9);
            opacity: 0.58;
          }
          18% {
            transform: scale(1.04);
            opacity: 0.72;
          }
          49% {
            transform: scale(1.18);
            opacity: 1;
          }
          74% {
            transform: scale(0.82);
            opacity: 0.66;
          }
          92% {
            transform: scale(1.08);
            opacity: 0.86;
          }
        }

        @keyframes reaction-giant-enter {
          0% {
            opacity: 0;
            transform: translateY(8px) scale(0.34) rotate(-2.2deg);
            filter: blur(2.2px);
          }
          26% {
            opacity: 0.82;
            transform: translateY(-6px) scale(1.02) rotate(1.4deg);
            filter: blur(0.7px);
          }
          54% {
            opacity: 1;
            transform: translateY(2px) scale(1.1) rotate(-0.8deg);
            filter: blur(0px);
          }
          78% {
            opacity: 1;
            transform: translateY(-1px) scale(0.985) rotate(0.25deg);
            filter: blur(0px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1) rotate(0deg);
            filter: blur(0px);
          }
        }

        @keyframes reaction-giant-exit {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1) rotate(0deg);
            filter: blur(0px);
          }
          34% {
            opacity: 0.95;
            transform: translateY(-4px) scale(0.97) rotate(-0.35deg);
            filter: blur(0.2px);
          }
          100% {
            opacity: 0;
            transform: translateY(-22px) scale(0.76) rotate(-1.2deg);
            filter: blur(2px);
          }
        }

        @keyframes reaction-giant-particle {
          0% {
            opacity: 0;
            top: 94%;
            transform: translate3d(-50%, 6px, 0)
              scale(calc(var(--rs, 0.6) * 0.78));
            filter: blur(0.8px);
          }
          16% {
            opacity: 0.72;
            top: 86%;
            transform: translate3d(calc(-50% + var(--sway, 8px) * 0.1), 2px, 0)
              scale(calc(var(--rs, 0.6) * 0.9));
            filter: blur(0);
          }
          38% {
            opacity: 0.88;
            top: 68%;
            transform: translate3d(calc(-50% + var(--sway, 8px) * 0.26), 0, 0)
              scale(var(--rs, 0.6));
          }
          62% {
            opacity: 0.82;
            top: 46%;
            transform: translate3d(calc(-50% + var(--sway, 8px) * 0.4), 0, 0)
              scale(calc(var(--rs, 0.6) * 0.98));
          }
          84% {
            opacity: 0.48;
            top: 24%;
            transform: translate3d(calc(-50% + var(--sway, 8px) * 0.5), 0, 0)
              scale(calc(var(--rs, 0.6) * 0.94));
            filter: blur(0.2px);
          }
          100% {
            opacity: 0;
            top: 10%;
            transform: translate3d(calc(-50% + var(--sway, 8px) * 0.55), 0, 0)
              scale(calc(var(--rs, 0.6) * 0.9));
            filter: blur(0.6px);
          }
        }

        .reaction-giant-pop-wrap {
          overflow: hidden;
          will-change: transform, opacity, filter;
          backface-visibility: hidden;
          transform-style: preserve-3d;
        }

        .reaction-giant-pop {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: transform, opacity, filter;
          backface-visibility: hidden;
          transform-origin: center;
          transform-style: preserve-3d;
        }

        .reaction-giant-particles {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          overflow: hidden;
          -webkit-mask-image: linear-gradient(
            to top,
            transparent 0%,
            rgb(0 0 0 / 0.45) 5%,
            #000 12%,
            #000 90%,
            rgb(0 0 0 / 0.55) 96%,
            transparent 100%
          );
          mask-image: linear-gradient(
            to top,
            transparent 0%,
            rgb(0 0 0 / 0.45) 5%,
            #000 12%,
            #000 90%,
            rgb(0 0 0 / 0.55) 96%,
            transparent 100%
          );
        }

        .reaction-giant-pop--enter {
          animation: reaction-giant-enter 920ms cubic-bezier(0.22, 1, 0.36, 1)
            both;
        }

        .reaction-giant-pop--exit {
          animation: reaction-giant-exit ${GIANT_REACTION_EXIT_MS}ms
            cubic-bezier(0.32, 0, 0.2, 1) both;
        }

        .reaction-giant-particle {
          position: absolute;
          left: var(--px, 50%);
          top: var(--py, 97%);
          line-height: 1;
          opacity: 0;
          will-change: top, transform, opacity;
          backface-visibility: hidden;
        }

        @media (prefers-reduced-motion: reduce) {
          .reaction-giant-pop--enter {
            animation-duration: 360ms;
          }
          .reaction-giant-pop--exit {
            animation-duration: 300ms;
          }
          .reaction-giant-particle {
            animation-duration: 520ms !important;
            animation-timing-function: ease-out !important;
          }
        }
      `}</style>

      <ReactionBurstOverlay burst={reactionBurst} />

      <ExploreReactionsModalV2
        open={isReactionsModalOpen}
        onClose={() => setIsReactionsModalOpen(false)}
        id={messageId}
        type="message"
      />

      <Modal
        open={shareWithColleagueOpen}
        onClose={handleCloseShareWithColleague}
        size="sm"
        eyebrow={t("Compartir")}
        title={t("Compartir con colegas")}
        description={t("Selecciona un colega para enviarle esta publicación por chat")}
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseShareWithColleague}
              disabled={creatingShareConversation || sendingSharedMessage}
            >
              {t("Cancelar")}
            </Button>
            <Button
              type="button"
              onClick={handleShareWithColleague}
              disabled={
                selectedShareColleagueIds.length === 0 ||
                creatingShareConversation ||
                sendingSharedMessage
              }
            >
              {creatingShareConversation || sendingSharedMessage
                ? t("Enviando...")
                : t("Enviar")}
            </Button>
          </div>
        }
      >
        <MultiSelectFilterV2
          options={chatUserOptions}
          value={selectedShareColleagueIds}
          onChange={(next) => {
            // Usamos el multiselect v2 como picker visual, pero este flujo es
            // uno-a-uno: guardamos solo la última selección.
            setSelectedShareColleagueIds(next.slice(-1));
          }}
          placeholder={t("Seleccionar colega")}
          searchPlaceholder={t("Buscar colega...")}
          emptyText={t("Sin colegas")}
          clearLabel={t("Limpiar")}
          selectAllLabel={t("Seleccionar visibles")}
          loading={chatUsersLoading}
          variant="violet"
          size="filter"
          compactPanelOnly
          label={t("Colega")}
          icon={<User className="h-4 w-4" />}
          footerCountLabel={({ total }) => `${total} ${t("colegas")}`}
        />
      </Modal>

      <div
        ref={footerRef}
        className="overflow-visible border-t border-slate-100/80 dark:border-zinc-800/60"
      >
        {/* ── Reaction bar ──────────────────────────────────────────────── */}
        {/* Una sola línea en todos los breakpoints. En mobile el slider de
          avatares reduce cuántas personas muestra para dejar espacio a las
          acciones de reacción/share/delete. */}
        <div className="relative z-20 flex min-h-[44px] flex-row items-center justify-between gap-2 overflow-visible px-4 py-2">
          {/* Left: avatar slider */}
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-visible">
            <ReactionAvatarSlider
              reactions={displayReactions}
              onOpen={handleOpenReactions}
            />
            <ExploreViewCounterV2
              count={timesSeen ?? 0}
              render={
                type === (EMessageType.CAPSULES as any) ||
                type === (EMessageType.MAGAZINE as any)
              }
              messageId={messageId}
            />
          </div>

          {/* Right: actions */}
          <div className="ml-auto flex shrink-0 items-center justify-end gap-0.5">
            {/* Reactions */}
            <div className="rounded-full bg-slate-50/70 px-1 py-0.5 dark:bg-zinc-900/50">
              <LiquidPillGroup
                activeValue={userReactionValue ?? null}
                size="sm"
                className="gap-0.5 [&_.lpg-item>span]:flex [&_.lpg-item>span]:h-full [&_.lpg-item>span]:w-full [&_.lpg-item>span]:items-center [&_.lpg-item>span]:justify-center [&_.lpg-item>span]:leading-none [&_.lpg-item]:flex [&_.lpg-item]:h-7 [&_.lpg-item]:w-7 [&_.lpg-item]:items-center [&_.lpg-item]:justify-center [&_.lpg-item]:px-0 [&_.lpg-item]:py-0"
                items={INLINE_REACTIONS.map((emoji) => ({
                  value: emoji,
                  icon: (
                    <span className="flex h-full w-full items-center justify-center text-[15px] leading-none">
                      {EMOJI_MAP[emoji]}
                    </span>
                  ),
                  variant:
                    emoji === "love"
                      ? "rose"
                      : emoji === "haha"
                        ? "gold"
                        : emoji === "wow"
                          ? "sunset"
                          : emoji === "sad"
                            ? "sky"
                            : emoji === "angry"
                              ? "coral"
                              : "ocean"
                }))}
                onChange={(emoji) => {
                  if (userReactionValue === emoji) {
                    handleRemoveReaction();
                    return;
                  }
                  handleReactionSelect(emoji);
                }}
              />
            </div>

            {/* Share */}
            <LiquidPillDropdown
              items={shareDropdownItems}
              onSelect={handleShareDropdownSelect}
              icon={<Share2 className="h-3.5 w-3.5" />}
              variant="slate"
              size="sm"
              collapsedShape="circle"
              listSize="xs"
              menuMaxHeight={360}
              menuZIndex={Z_INDEX.DROPDOWN}
              aria-label={t("Compartir publicación")}
            />

            {/* Delete message */}
            {user?.isi && onDeleteMessage && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-slate-400 hover:bg-red-50 hover:text-red-500 dark:text-zinc-500 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                disabled={deleteLoading}
                onClick={onDeleteMessage}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* ── Comments section ──────────────────────────────────────────── */}
        <div className="relative z-0 pb-3 pt-0">
          {/* "Ver más" toggle */}
          {olderCount > 0 && (
            <div className="px-4 pb-1">
              <button
                onClick={() => setShowOlder(!showOlder)}
                className="text-[11px] font-medium text-slate-500 hover:text-indigo-600 dark:text-zinc-400 dark:hover:text-indigo-300"
              >
                {showOlder
                  ? t("Ver menos comentarios")
                  : `↑ ${olderCount} ${t("comentarios anteriores")}`}
              </button>
            </div>
          )}

          {/* Older comments */}
          {showOlder && (
            <div className="space-y-0.5 px-4">
              {olderComments.map((c) => (
                <CommentRow
                  key={c.id}
                  comment={c}
                  usersToMention={usersToMention}
                  onDelete={handleDeleteComment}
                  deleteLoading={deleteCommentLoading}
                  deletingId={deletingId}
                  serialize={serialize}
                  lang={lang}
                />
              ))}
              {olderCount > 0 && (
                <div className="my-0.5 border-t border-slate-100/80 dark:border-zinc-800/60" />
              )}
            </div>
          )}

          {/* Recent comments */}
          {commentList.length > 0 && (
            <div className="space-y-0.5 px-4">
              {recentComments.map((c) => (
                <CommentRow
                  key={c.id}
                  comment={c}
                  usersToMention={usersToMention}
                  onDelete={handleDeleteComment}
                  deleteLoading={deleteCommentLoading}
                  deletingId={deletingId}
                  serialize={serialize}
                  lang={lang}
                />
              ))}
            </div>
          )}

          {/* Comment input */}
          <div className="mt-2 px-4">
            {!inputOpen ? (
              /* Collapsed pill */
              <div className="flex items-center gap-2">
                <div className="shrink-0">
                  <UserAvatar user={user ?? undefined} size={26} />
                </div>
                <button
                  onClick={() => setInputOpen(true)}
                  className="flex h-8 flex-1 items-center rounded-full border border-slate-200/70 bg-slate-50/90 px-4 text-left text-[11.5px] text-slate-400 transition-all duration-200 hover:border-indigo-300/60 hover:bg-white dark:border-zinc-800/80 dark:bg-zinc-900/60 dark:text-zinc-500 dark:hover:border-indigo-500/40"
                >
                  {t("Escribe un comentario")}...
                </button>
              </div>
            ) : (
              /* Expanded editor */
              <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white dark:border-zinc-700/60 dark:bg-zinc-900">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-100/80 px-3 py-1.5 dark:border-zinc-800/60">
                  <div className="flex items-center gap-1.5">
                    <UserAvatar user={user ?? undefined} size={20} />
                    <span className="text-[11px] font-medium text-slate-700 dark:text-zinc-300">
                      {user?.name} {user?.lastname}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setInputOpen(false);
                      setIsUploadingCommentImage(false);
                      setPendingCommentImageCount(0);
                    }}
                    className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-zinc-800"
                  >
                    <X size={13} />
                  </button>
                </div>

                {/* Editor */}
                <div className="min-h-[56px] px-3 py-2">
                  <Controller
                    name="message"
                    control={control}
                    render={({ field }) => (
                      <Note3
                        {...field}
                        onChange={(value) => {
                          field.onChange(value);
                          handleCommentChange(value);
                        }}
                        onImageUploadChange={(loading, pendingCount = 0) => {
                          setIsUploadingCommentImage(loading);
                          setPendingCommentImageCount(pendingCount);
                        }}
                        onImageRemove={handleDeleteCommentImageFromStorage}
                        imageRemoveHandlerRef={removeCommentImageRef}
                        placeholder={t("Escribe un comentario")}
                        usersToMention={usersToMention}
                        compact
                        showIAButton={false}
                      />
                    )}
                  />
                </div>

                <ExploreCommentMediaPreviewV2
                  imageUrls={commentPreviewImages}
                  loading={isUploadingCommentImage}
                  pendingCount={pendingCommentImageCount}
                  removingUrl={removingCommentImageUrl}
                  onRemove={handleRemoveCommentImage}
                />

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-slate-100/80 px-3 py-1.5 dark:border-zinc-800/60">
                  <span className="hidden text-[10px] text-slate-400 dark:text-zinc-500 sm:inline">
                    @ {t("para mencionar")} · {t("Pega imágenes con")} ⌘V
                  </span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => setInputOpen(false)}
                    >
                      {t("Cancelar")}
                    </Button>
                    <Button
                      size="sm"
                      className="h-6 gap-1 px-2.5 text-[11px]"
                      disabled={
                        contentEmpty ||
                        isUploadingCommentImage ||
                        Boolean(removingCommentImageUrl)
                      }
                      onClick={handleSubmit(handlePublish)}
                    >
                      {createLoading ? (
                        "..."
                      ) : (
                        <>
                          <Send size={11} />
                          {t("Publicar")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
